import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function ProtectedRoute({ children }) {
  const { user, token, tokenExpiresAt, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Matches App.js's RouteFallback and every protected page's own
    // bg-white - see that component's comment for why (docs/DECISIONS.md,
    // "Bug-report batch: session/exam-flow/notification/curriculum
    // fixes"). This used to be a dark slate-900 screen, one of three
    // different dark/light backgrounds a student could flash through on
    // a single page load.
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="h-10 w-10 rounded-full border-2 border-brand-blue border-t-transparent animate-spin" />
      </div>
    );
  }

  // Require both `user` and a real `token` - a `user` with no `token` is a
  // stale/degraded session (e.g. left over from before the auth-server was
  // reachable) that would otherwise pass this check and only fail later,
  // deep inside a page, as a confusing "missing token" API error instead of
  // a clean redirect to /login (docs/DECISIONS.md, 2026-07-29 "QuizPortal
  // exam-link access: ProtectedRoute didn't check for a real token").
  // Carry the originally-requested location through to /login (e.g. an
  // exam link opened while logged out) so Login.jsx can send the user back
  // there after a successful sign-in instead of always landing on
  // /curriculum (docs/DECISIONS.md, 2026-07-29 "Login redirect target after
  // an exam-link login").
  // A token can be present but already expired - e.g. an emailed exam link
  // opened well after AUTH_REFRESH_JWT_TTL_SECONDS (15 min by default) has
  // passed since the student last logged in. Presence alone used to be
  // enough to pass this check, so a stale token reached QuizPortal and
  // surfaced as a raw "invalid_token" string from POST .../start instead of
  // a clean redirect back to /login (docs/DECISIONS.md, 2026-08-08
  // "exam-flow bug batch #5: stale token on exam-link click"). `expiresAt`
  // is a unix-seconds timestamp (server/lambdas/shared/jwt.js's
  // signServerSessionToken uses the JWT `exp` claim directly).
  const tokenExpired = tokenExpiresAt && tokenExpiresAt <= Math.floor(Date.now() / 1000);
  if (!user || !token || tokenExpired) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return children;
}
